import { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL, fetchWithCSRF } from '../config';

/**
 * Custom hook for managing AI recommendations
 */
export function useAIRecommendations(uuid, formData, recommendations) {
  const [recommendedSchools, setRecommendedSchools] = useState({
    reach: [],
    target: [],
    safety: []
  });
  const [schoolExplanations, setSchoolExplanations] = useState({});
  const [loadingAIRecommendations, setLoadingAIRecommendations] = useState(true);
  const [prefetchedSchools, setPrefetchedSchools] = useState({
    reach: null,
    target: null,
    safety: null
  });

  // Fetch initial AI recommendations
  useEffect(() => {
    const fetchAIRecommendations = async () => {
      if (!formData || !recommendations) return;

      setLoadingAIRecommendations(true);

      // Only fetch if there are more than 9 schools in any category
      const needsRanking =
        (recommendations.reach && recommendations.reach.length > 9) ||
        (recommendations.target && recommendations.target.length > 9) ||
        (recommendations.safety && recommendations.safety.length > 9);

      if (!needsRanking) {
        // If no category has more than 9 schools, mark all as recommended
        setRecommendedSchools({
          reach: recommendations.reach ? recommendations.reach.map(s => s.id) : [],
          target: recommendations.target ? recommendations.target.map(s => s.id) : [],
          safety: recommendations.safety ? recommendations.safety.map(s => s.id) : []
        });
        setLoadingAIRecommendations(false);
        return;
      }

      try {
        const studentProfile = {
          gpa: formData.gpa,
          sat_score: formData.satScore,
          act_score: formData.actScore,
          grade: formData.grade,
          state: formData.state
        };

        const schoolsByCategory = {
          reach: recommendations.reach || [],
          target: recommendations.target || [],
          safety: recommendations.safety || []
        };

        const interestedSchools = formData.schools
          ? formData.schools.split(',').map(s => s.trim()).filter(s => s.length > 0)
          : [];

        const response = await fetchWithCSRF(`${API_BASE_URL}/api/universities/ai/rank-schools/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            student_profile: studentProfile,
            schools_by_category: schoolsByCategory,
            interested_schools: interestedSchools,
            report_uuid: uuid
          })
        });

        if (response.ok) {
          const data = await response.json();
          setRecommendedSchools(data.recommended_schools);
          setSchoolExplanations(data.school_explanations || {});
        } else {
          // Fallback: mark first 9 of each category as recommended
          setRecommendedSchools({
            reach: recommendations.reach ? recommendations.reach.slice(0, 9).map(s => s.id) : [],
            target: recommendations.target ? recommendations.target.slice(0, 9).map(s => s.id) : [],
            safety: recommendations.safety ? recommendations.safety.slice(0, 9).map(s => s.id) : []
          });
        }
      } catch (error) {
        // Fallback
        setRecommendedSchools({
          reach: recommendations.reach ? recommendations.reach.slice(0, 9).map(s => s.id) : [],
          target: recommendations.target ? recommendations.target.slice(0, 9).map(s => s.id) : [],
          safety: recommendations.safety ? recommendations.safety.slice(0, 9).map(s => s.id) : []
        });
      } finally {
        setLoadingAIRecommendations(false);
      }
    };

    fetchAIRecommendations();
  }, [uuid, formData, recommendations]);

  // Prefetch next batch for a category
  const prefetchNextBatch = useCallback(async (category, currentCount) => {
    if (!formData || !recommendations) return;

    try {
      const studentProfile = {
        gpa: formData.gpa,
        sat_score: formData.satScore,
        act_score: formData.actScore,
        grade: formData.grade,
        state: formData.state
      };

      const categorySchools = recommendations[category] || [];
      const interestedSchools = formData.schools
        ? formData.schools.split(',').map(s => s.trim()).filter(s => s.length > 0)
        : [];

      const requestBody = {
        student_profile: studentProfile,
        schools_by_category: {
          [category]: categorySchools
        },
        interested_schools: interestedSchools,
        report_uuid: uuid,
        top_n: currentCount + 9
      };

      const response = await fetchWithCSRF(`${API_BASE_URL}/api/universities/ai/rank-schools/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const data = await response.json();
        setPrefetchedSchools(prev => ({
          ...prev,
          [category]: {
            recommended_schools: data.recommended_schools[category] || [],
            school_explanations: data.school_explanations || {},
            for_count: currentCount + 9
          }
        }));
      }
    } catch (error) {
      console.error('Error prefetching schools:', error);
    }
  }, [formData, recommendations, uuid]);

  // Prefetch initial batches after AI recommendations are loaded
  useEffect(() => {
    if (loadingAIRecommendations || !formData || !recommendations) return;

    const timer = setTimeout(() => {
      if (recommendations.reach && recommendations.reach.length > 9) {
        prefetchNextBatch('reach', 9);
      }
      if (recommendations.target && recommendations.target.length > 9) {
        prefetchNextBatch('target', 9);
      }
      if (recommendations.safety && recommendations.safety.length > 9) {
        prefetchNextBatch('safety', 9);
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [loadingAIRecommendations, formData, recommendations, prefetchNextBatch]);

  return {
    recommendedSchools,
    setRecommendedSchools,
    schoolExplanations,
    setSchoolExplanations,
    loadingAIRecommendations,
    prefetchedSchools,
    setPrefetchedSchools,
    prefetchNextBatch
  };
}